import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview RefineTasteCard — LOCI floating refine-taste card (FRE-774).
 *
 * A glass-surface card with three sliders (Energy, Distance, Price) that
 * emits taste-preference changes upstream. The card is purely controlled;
 * callers own state, debouncing, and any downstream refetch wiring.
 *
 * Layout:
 *   - `REFINE MY TASTE` eyebrow (left) + Reset button (amber outline, right)
 *   - Three slider rows: label (left) + mono value (right) + label-pair hint
 *   - Glass background + 1px white-10 border
 *   - Amber thumb on teal track (see TasteSlider)
 */
import { forwardRef, useId } from 'react';
import { useTheme } from '../../../theme';
import { TasteSlider } from './TasteSlider';
import { DEFAULT_TASTE_PREFERENCES, } from './RefineTasteCard.types';
const ENERGY_LABELS = ['Chill', 'Warehouse'];
const DISTANCE_LABELS = ['Walk', '10 mi'];
const PRICE_LABELS = ['Budget', 'Premium'];
const PRICE_GLYPHS = ['$', '$$', '$$$', '$$$$'];
function formatEnergy(energy) {
    return `${Math.round(energy * 100)}%`;
}
function formatDistance(miles) {
    if (miles < 1)
        return `${miles.toFixed(1)} mi`;
    return `${miles.toFixed(miles % 1 === 0 ? 0 : 1)} mi`;
}
function formatPrice(price) {
    const idx = Math.max(1, Math.min(PRICE_GLYPHS.length, Math.round(price))) - 1;
    return PRICE_GLYPHS[idx];
}
export { DEFAULT_TASTE_PREFERENCES };
export const RefineTasteCard = forwardRef(function RefineTasteCard({ value, onChange, onReset, className, style }, ref) {
    const theme = useTheme();
    const reactId = useId();
    const energyLabelId = `refine-taste-${reactId}-energy`;
    const distanceLabelId = `refine-taste-${reactId}-distance`;
    const priceLabelId = `refine-taste-${reactId}-price`;
    const cardStyle = {
        position: 'relative',
        borderRadius: 16,
        padding: '16px 18px',
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.10)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
        color: theme.colors.text,
    };
    const headerStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
    };
    // Mirrors the Eyebrow atom's tracked-caps treatment but renders as a real
    // <h2> so the surrounding section exposes a proper heading hierarchy
    // (WEB-149).
    const headingStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.semibold,
        fontSize: theme.typography.fontSize.caption,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: theme.colors.accent,
    };
    const resetButtonStyle = {
        padding: '4px 10px',
        borderRadius: 9999,
        border: `1px solid ${theme.colors.accent}`,
        background: 'transparent',
        color: theme.colors.accent,
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: 11,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        fontWeight: theme.typography.fontWeight.semibold,
        cursor: 'pointer',
    };
    const sliderRowStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    };
    const labelRowStyle = {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
    };
    const labelStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: 13,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text,
        letterSpacing: '0.01em',
    };
    const valueStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: 12,
        color: theme.colors.textMuted,
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
        whiteSpace: 'nowrap',
    };
    const hintStyle = {
        marginTop: 2,
        fontFamily: theme.typography.fontFamily,
        fontSize: 11,
        color: theme.colors.textMuted,
        letterSpacing: '0.02em',
    };
    const rowsStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    };
    return (_jsxs("section", { ref: ref, "aria-label": "Refine taste preferences", className: className, style: { ...cardStyle, ...style }, "data-testid": "refine-taste-card", children: [_jsxs("div", { style: headerStyle, children: [_jsx("h2", { style: headingStyle, children: "Refine my taste" }), _jsx("button", { type: "button", onClick: onReset, style: resetButtonStyle, "data-testid": "refine-taste-reset", className: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", children: "Reset" })] }), _jsxs("div", { style: rowsStyle, children: [_jsxs("div", { style: sliderRowStyle, "data-testid": "refine-taste-row-energy", children: [_jsxs("div", { style: labelRowStyle, children: [_jsx("span", { id: energyLabelId, style: labelStyle, children: "Energy" }), _jsx("span", { style: valueStyle, "data-testid": "refine-taste-value-energy", children: formatEnergy(value.energy) })] }), _jsx(TasteSlider, { ariaLabel: "Energy", labelledBy: energyLabelId, min: 0, max: 1, step: 0.05, value: value.energy, onValueChange: (energy) => onChange({ ...value, energy }), "data-testid": "refine-taste-slider-energy" }), _jsxs("span", { style: hintStyle, "aria-hidden": "true", children: [ENERGY_LABELS[0], " \u00B7 ", ENERGY_LABELS[1]] })] }), _jsxs("div", { style: sliderRowStyle, "data-testid": "refine-taste-row-distance", children: [_jsxs("div", { style: labelRowStyle, children: [_jsx("span", { id: distanceLabelId, style: labelStyle, children: "Distance" }), _jsx("span", { style: valueStyle, "data-testid": "refine-taste-value-distance", children: formatDistance(value.maxDistanceMiles) })] }), _jsx(TasteSlider, { ariaLabel: "Maximum distance in miles", labelledBy: distanceLabelId, min: 0.5, max: 10, step: 0.5, value: value.maxDistanceMiles, onValueChange: (maxDistanceMiles) => onChange({ ...value, maxDistanceMiles }), "data-testid": "refine-taste-slider-distance" }), _jsxs("span", { style: hintStyle, "aria-hidden": "true", children: [DISTANCE_LABELS[0], " \u00B7 ", DISTANCE_LABELS[1]] })] }), _jsxs("div", { style: sliderRowStyle, "data-testid": "refine-taste-row-price", children: [_jsxs("div", { style: labelRowStyle, children: [_jsx("span", { id: priceLabelId, style: labelStyle, children: "Price" }), _jsx("span", { style: valueStyle, "data-testid": "refine-taste-value-price", children: formatPrice(value.maxPrice) })] }), _jsx(TasteSlider, { ariaLabel: "Maximum price tier", labelledBy: priceLabelId, min: 1, max: 4, step: 1, value: value.maxPrice, onValueChange: (maxPrice) => onChange({ ...value, maxPrice }), "data-testid": "refine-taste-slider-price" }), _jsxs("span", { style: hintStyle, "aria-hidden": "true", children: [PRICE_LABELS[0], " \u00B7 ", PRICE_LABELS[1]] })] })] })] }));
});
